/* ExportAction.js */

"use strict";
const _ = require("lodash");
const ExportDataVeilComponent = require("./exportdata/ExportDataVeilComponent");
const moment = require("moment-timezone");
const FileName = require("@gluenetworks/steve_utilities").fileName;

class ExportAction {
    constructor(logger, gameEditor, activeView, steveIo, userSession, solutionsManager) {
        this._logger = logger;
        this._activeView = activeView;
        this._fileFactory = steveIo.fileFactory;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._fileNamer = FileName;
        this._solutionName = _.get(solutionsManager, "activeSolution.name");
        this.run = this.run.bind(this);
    }

    dataToInt8Arr(data) {
        const byteString = window.atob(data);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const int8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
            int8Array[i] = byteString.charCodeAt(i);
        }
        return int8Array;
    }

    run() {
        const expComp = new ExportDataVeilComponent(this._logger, {
            gameEditor: this._gameEditor,
            fileFactory: this._fileFactory,
            soln: this._activeView._solutionName,
        });

        let templateEntity, execution, template, workTarget, workInput, type;
        return this._gameEditor._modalUserFeedback
            .displayCustom(expComp, {dontHide: false})
            .then(({entity, _template, _workTarget, _workInput, _type}) => {
                template = _template;
                workTarget = _workTarget;
                workInput = _workInput;
                type = _type;
                workInput.notifyReportTypes = {
                    csv: true,
                    pdf: true,
                    json: true,
                };
                templateEntity = entity;
                return this._gameEditor._modalUserFeedback.hideDisplayed();
            })
            .then(() => {
                if (type === "schedule") {
                    return;
                }
                this._gameEditor._modalUserFeedback.displayLoading({
                    message: "Running template...",
                    showHideButton: true,
                });
                this._gameEditor._modalUserFeedback.showLoadingProgress("0%");

                let workCall = "runExploreWork";
                switch (this._solutionName) {
                    case "device_manager":
                        workCall = "runDmExploreWork";
                        break;
                    case "config_drift":
                        workCall = "runCdaExploreWork";
                        break;
                    case "os_manager":
                        workCall = "runOsmExploreWork";
                        break;
                }

                return _.invoke(
                    template,
                    workCall,
                    _.get(this._userSession, "activeOrganization.id"),
                    workTarget,
                    workInput,
                    {},
                    (progress) => {
                        const percent = parseInt(
                            (typeof progress.targetCount === "number" && progress.targetCount > 0
                                ? (progress.successCount +
                                      progress.failureCount +
                                      progress.missingTargetCount) /
                                  progress.targetCount
                                : 0) * 100,
                            10
                        );
                        this._gameEditor._modalUserFeedback.showLoadingProgress(`${percent || 0}%`);
                    }
                )
                    .then(() => {
                        return templateEntity.loadRelatedExecutions({
                            ownerUserId: _.get(this._userSession, "user.id"),
                            tid: _.get(this._userSession, "activeOrganization.id"),
                        });
                    })
                    .then((exec) => {
                        if (exec.length <= 0) {
                            throw {errorMsg: "Execution failed"};
                        }
                        if (exec[0].status === "FAILED") {
                            const errorMessage = exec[0].errorMsg || "Execution failed";
                            this._logger.warn({error: errorMessage}, "ExportData action - Error");
                            throw {errorMsg: errorMessage};
                        }
                        if (this._activeView._solutionName === "data_explorer") {
                            this._gameEditor._modalUserFeedback.showLoadingProgress(
                                "Opening results view... "
                            );
                        } else {
                            this._gameEditor._modalUserFeedback.showLoadingProgress(
                                "Downloading... "
                            );
                        }

                        if (this._activeView._solutionName === "data_explorer") {
                            this._gameEditor._modalUserFeedback.hideDisplayed();
                            const editorState = "result";
                            const launchOpts = {
                                id: templateEntity.id,
                                title: templateEntity.properties.name,
                            };
                            this._gameEditor.launchDataExplorerView(editorState, launchOpts);
                            throw "done"; // throwing an error to break the chain as further processing not required
                        }
                        execution = exec[0];
                        return exec[0].loadRelatedWork();
                    })
                    .then((work) => {
                        // console.log(work);
                        return work.loadRelatedReports({reportType: type});
                    })
                    .then((rep) => {
                        // console.log(rep);
                        if (!(rep && rep[0])) {
                            this._gameEditor._modalUserFeedback.hideDisplayed();
                            throw {errorMsg: "Execution failed :: No report generated"};
                        }
                        const _templateName = templateEntity.name,
                            _execDate = new Date(execution.executedOn);
                        const timeZone = moment.tz.guess(),
                            _fileNameTime = moment
                                .tz(_execDate.toISOString(), timeZone)
                                .format("YYYYMMDD_HHmmss_z"),
                            _fileName = `DE_Results_${_templateName}_${_fileNameTime}`;
                        switch (type) {
                            case "json":
                                this._fileFactory.saveJsonAsFile(
                                    rep[0].report.results,
                                    _fileName + ".json"
                                );
                                break;
                            case "pdf":
                                this._fileFactory.saveArrayBufferAsFile(
                                    this.dataToInt8Arr(rep[0].report),
                                    _fileName + ".pdf",
                                    "application/pdf"
                                );
                                break;
                            default:
                                this._fileFactory.saveStringAsFile(
                                    rep[0].report,
                                    _fileName + ".csv",
                                    "text/csv"
                                );
                                break;
                        }
                        return this._gameEditor._modalUserFeedback.hideDisplayed();
                    })
                    .catch((err) => {
                        if (err === "done") {
                            // if done do nothing as this is not an actual error
                            this._logger.info("ExportData Done :: Opening result view");
                            return;
                        }
                        this._logger.warn({error: err}, "ExportData action - Error");
                        return this._gameEditor._modalUserFeedback.hideDisplayed().then(() => {
                            return this._gameEditor._modalUserFeedback.displayError(err);
                        });
                    });
            })
            .catch((err) => {
                if (err && err.userRejected) {
                    this._logger.warn({error: err}, "ExportData action - User Canceled");
                    return;
                } else {
                    this._logger.warn({error: err}, "ExportData action - Error");
                    return this._gameEditor._modalUserFeedback.hideDisplayed().then(() => {
                        return this._gameEditor._modalUserFeedback.displayError(err);
                    });
                }
            });
    }
}

module.exports = ExportAction;

ExportAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.activeView",
    "$teve.io",
    "$teve.userSession",
    "$teve.solutionsManager",
];

ExportAction.$displayDetails = {
    type: "Action",
    name: "ExportAction",
    icon: "dataexplorer.run",
    shown: true,
    title: "Run Report",
    order: 15,
};
